import { Contract } from '../validationSchemas/ContractSchema';
import { addCommas, formatServerDate, round } from './helpers';
import { camelCaseKeys } from './object';

const mapContractToForm = (contractData, advertiserData = null): Contract => {
  const contractStatus = contractData.derived_ui_state || 'incomplete';
  const clientAdvEditable = ['incomplete', 'scheduled', 'qa_hold'].includes(contractStatus);

  const selectedClientName = advertiserData?.client?.name || contractData.client_name || '';
  const selectedAdvertiserName = advertiserData?.name || contractData.advertiser_name || '';
  const selectedClientId = contractData.client_id || advertiserData?.client?.id || 0;
  const selectedAdvertiserId = contractData.advertiser_id || 0;

  const mapped: Contract = {
    ...camelCaseKeys(contractData),
    contractId: contractData.id,
    contractName: contractData.name || '',
    ioFile: contractData.io_path || '',
    startDate: contractData.start_date ? formatServerDate(contractData.start_date) : '',
    endDate: contractData.end_date ? formatServerDate(contractData.end_date) : '',
    bidType: contractData.bid_type || 'CPM',
    bidSourceType: contractData.bid_source_type || 'STANDARD',
    pricingRate: round(
      contractData.uses_cpc
        ? (contractData.contract_cpc / 100).toString() || 0
        : (contractData.contract_cpm / 100).toString() || 0
    ),
    contractCpm: contractData.contract_cpm || 0,
    contractCpc: contractData.contract_cpc || 0,
    advisoryBudget: contractData.advisory_budget ? addCommas(contractData.advisory_budget) : '0',
    bonus: contractData.bonus ? addCommas(contractData.bonus) : '0',
    dollarBudget: (contractData.dollar_budget && round(contractData.dollar_budget)) || '0',
    pacingStrategies: contractData.pacing_strategies || [],
    slowStart: contractData.pacing_strategies ? contractData.pacing_strategies.includes('slowstart') : false,
    fastCatchup: contractData.pacing_strategies ? contractData.pacing_strategies.includes('fastcatchup') : false,
    attributionFile: contractData.attribution_targets_upload_path || '',
    categorization: contractData.categorization || [],
    isComplete: contractData.complete || false,
    storeList: contractData.store_list?.split(', ') || [],
    cpaAction: contractData.cpa_action?.split(',') || [],
    // kpi_goal will be in this format: "lorem:ipsum;asdf:qwer;"
    kpiGoalFieldArray:
      contractData.kpi_goal
        ?.replace(/;$/, '')
        .split(';')
        .map((itemString) => ({ key: itemString.split(':')[0], value: itemString.split(':')[1] })) || [],

    selectedClientName,
    selectedAdvertiserName,
    selectedClientId,
    selectedAdvertiserId,
    billingModel: 'managedservice',
    frequencyCapEnabled: !!contractData.frequency_capping,
    frequencyCapping: contractData?.frequency_capping || 'daily',
    maxUserViewsPerPeriod: contractData?.max_user_views_per_period || '',
    performanceCapEnabled: !!contractData.click_frequency_capping,
    clickFrequencyCapping: contractData?.click_frequency_capping || 'daily',
    maxUserClicksPerPeriod: contractData?.max_user_clicks_per_period || '',
    locked_for_advertiser_change: contractData.locked_for_advertiser_change ?? false,
    clientAdvEditable,
    customBillingOptions:
      contractData.custom_billing_options?.map((option) => ({
        ...camelCaseKeys(option),
        startDate: option.start_date ? formatServerDate(option.start_date) : null,
        endDate: option.end_date ? formatServerDate(option.end_date) : null,
      })) || [],

    contractLciCampaigns:
      (contractData.contract_lci_campaigns || [])
        .filter((row) => (row.campaign_type || 'internal') === 'internal')
        .map((row) => ({
          id: row.id,
          lci_campaign_id: Number(row.lci_campaign_id),
          campaign_type: 'internal',
          active: row.active !== false,
        })),

    contractKpis:
      contractData.contract_kpis?.map((kpi, index) => ({
        id: kpi.id,
        kpiName: kpi.kpi_name || '',
        provider: kpi.provider || '',
        goalValue: kpi.goal_value || '',
        goalFormat: kpi.goal_format || 'number',
        isCustomKpi: kpi.is_custom_kpi || false,
        isCustomProvider: kpi.is_custom_provider || false,
        position: kpi.position || index,
      })) || [],
  };

  if (
    (!mapped.contractLciCampaigns || mapped.contractLciCampaigns.length === 0) &&
    mapped.lciMeasurement &&
    contractData.lci_campaign_id != null
  ) {
    mapped.contractLciCampaigns = [{
      lci_campaign_id: Number(contractData.lci_campaign_id),
      campaign_type: 'internal',
      active: true,
    } as any];
  }

  return mapped;
};

export default mapContractToForm;
